import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { User, X, Plus, Check } from 'lucide-react';
import { useParadigm } from '../../contexts/ParadigmContext';

const AssignExecutiveModal = ({ trip, onClose, onUpdate }) => {
  const { labels } = useParadigm();
  const [allExecutives, setAllExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignedExecutiveIds, setAssignedExecutiveIds] = useState([]);

  useEffect(() => {
    loadExecutives();
    // Initialize assigned executives from trip data
    if (trip.executive_ids) {
      setAssignedExecutiveIds(trip.executive_ids);
    }
  }, [trip]);

  const loadExecutives = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/executives`, { headers });
      setAllExecutives(response.data || []);
    } catch (err) {
      setError('Failed to load executives');
    } finally {
      setLoading(false);
    }
  };

  const toggleExecutiveAssignment = (executiveId) => {
    setAssignedExecutiveIds(prev => {
      if (prev.includes(executiveId)) {
        return prev.filter(id => id !== executiveId);
      } else {
        return [...prev, executiveId];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      // Get executive names for the assigned IDs
      const assignedExecutives = allExecutives.filter(exec => 
        assignedExecutiveIds.includes(exec.id)
      );
      const executiveNames = assignedExecutives.map(exec => 
        exec.full_name || `${exec.first_name} ${exec.last_name}`
      );

      // Update the trip with new executive assignments
      await axios.put(`${apiBase}/trips/${trip.id}`, {
        executive_ids: assignedExecutiveIds,
        executive_names: executiveNames
      }, { headers });

      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update trip executives');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 h-full w-full z-[70] flex items-center justify-center">
      <div className="bg-ui-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-ui-blue-bg rounded-full flex items-center justify-center mr-3">
                <User className="w-5 h-5 text-ui-link" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-ui-ink">{labels.assignExecutives}</h3>
                <p className="text-sm text-ui-muted">{'Select ' + labels.executives.toLowerCase() + ' for ' + trip.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-3 mb-4">
              <div className="text-ui-red-fg text-sm">{error}</div>
            </div>
          )}

          {/* Executive Selection */}
          {loading ? (
            <div className="text-center py-8">
              <div className="text-ui-muted">{'Loading ' + labels.executives.toLowerCase() + '...'}</div>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {allExecutives.map(executive => {
                  const isAssigned = assignedExecutiveIds.includes(executive.id);
                  return (
                    <div
                      key={executive.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        isAssigned 
                          ? 'border-ui-blue-line bg-ui-blue-bg' 
                          : 'border-ui-line hover:border-ui-control hover:bg-ui-hover'
                      }`}
                      onClick={() => toggleExecutiveAssignment(executive.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-ui-hover rounded-full flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-ui-ink-3" />
                          </div>
                          <div>
                            <div className="font-medium text-ui-ink">
                              {executive.full_name || `${executive.first_name} ${executive.last_name}`}
                            </div>
                            <div className="text-sm text-ui-muted">
                              {executive.title && executive.title}
                              {executive.title && executive.email && ' • '}
                              {executive.email}
                            </div>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isAssigned 
                            ? 'border-ui-primary bg-ui-primary' 
                            : 'border-ui-control'
                        }`}>
                          {isAssigned && <Check className="w-4 h-4 text-ui-on-primary" />}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {allExecutives.length === 0 && (
                  <div className="text-center py-8">
                    <User className="w-16 h-16 text-ui-faint mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-ui-ink-3 mb-2">{labels.noExecutives}</h3>
                    <p className="text-ui-muted">{'Create ' + labels.executives.toLowerCase() + ' first to assign them to trips'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-ui-control rounded-md text-sm font-medium text-ui-ink-2 bg-ui-surface hover:bg-ui-hover transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-ui-on-primary bg-ui-primary hover:bg-ui-primary-hover transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : `Assign ${assignedExecutiveIds.length} ${assignedExecutiveIds.length === 1 ? labels.executive : labels.executives}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignExecutiveModal;